import { createSelector } from 'reselect';

import {
	CheckoutOrderFragment,
	CheckinOrderFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment as VipServiceFragment,
	GdsServiceVipSubType
} from '@websky/graphql';
import { SelectedServicesState } from '../../../../../Checkout/store/selectedServices/reducers';
import { ServicesByKey, ServicesByType } from '../model/types';

import { checkoutSelectedServicesStateFactory } from '../../../../../Checkout/utils';

function getCardExtraServicesByTypesSelector(additionalServices: OrderAdditionalServicesFragment) {
	const servicesByType: Partial<ServicesByType> = {};

	additionalServices?.gdsServices?.services?.forEach(service => {
		const key = service.rfisc;

		let type = service.type;

		if (service.type === OrderAdditionalServiceGdsServiceServiceType.Vip) {
			const { subType } = service as VipServiceFragment;

			if (
				subType === GdsServiceVipSubType.BusinessLounge ||
				subType === GdsServiceVipSubType.BusinessLoungeInternational
			) {
				type = OrderAdditionalServiceGdsServiceServiceType.BusinessLounge;
			}
		}

		if (!servicesByType[type]) {
			servicesByType[type] = {};
		}

		const existService = servicesByType[type][key];
		if (!existService || +existService.id < +service.id) {
			servicesByType[type][key] = service;
		}
	});

	return servicesByType;
}

function getSelectedServicesByIdSelector(selectedServices: SelectedServicesState) {
	const selectedServicesById: Record<string, boolean> = {};

	for (const passengerId of Object.keys(selectedServices)) {
		const passengerServices = selectedServices[passengerId];
		for (const serviceId of Object.keys(passengerServices)) {
			if (selectedServicesById[serviceId]) {
				continue;
			}

			selectedServicesById[serviceId] = !!passengerServices[serviceId].length;
		}
	}

	return selectedServicesById;
}

function getAvailableCardExtraServicesTypesSelector(
	cardExtraServicesByTypes: Partial<ServicesByType>,
	selectedServicesById: Record<string, boolean>
) {
	const availableCardExtraServicesTypes: Partial<Record<OrderAdditionalServiceGdsServiceServiceType, boolean>> = {};

	for (const type of Object.keys(cardExtraServicesByTypes)) {
		const servicesByKey = cardExtraServicesByTypes[type] as ServicesByKey;

		if (availableCardExtraServicesTypes[type] || !Object.keys(servicesByKey).length) {
			continue;
		}

		availableCardExtraServicesTypes[type] = Object.values(servicesByKey).some(
			service => service.canBeAdded || selectedServicesById[service.id]
		);
	}

	return availableCardExtraServicesTypes;
}

export function createCardExtraSelectors<T>(getOrder: (state: T) => CheckoutOrderFragment | CheckinOrderFragment) {
	const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);

	const getSelectedServicesState = createSelector(getOrder, checkoutSelectedServicesStateFactory);

	const getCardExtraServicesByTypes = createSelector(getAdditionalServices, getCardExtraServicesByTypesSelector);

	const getSelectedServicesById = createSelector(getSelectedServicesState, getSelectedServicesByIdSelector);

	const getAvailableCardExtraServicesTypes = createSelector(
		getCardExtraServicesByTypes,
		getSelectedServicesById,
		getAvailableCardExtraServicesTypesSelector
	);

	return {
		getCardExtraServicesByTypes,
		getSelectedServicesById,
		getAvailableCardExtraServicesTypes
	};
}
